import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const leadPrefill = (lead, amount) => ({
  customer_name: lead.name,
  customer_phone: lead.phone,
  customer_email: lead.email,
  job_title: lead.project,
  service: lead.service,
  line: { description: lead.service || "Service", amount },
});

export function useLeads(enabled = true) {
  const { credits, refreshCredits } = useAuth();
  const [service, setService] = useState("");
  const [location, setLocation] = useState("");
  const [leadType, setLeadType] = useState("all");
  const [searching, setSearching] = useState(false);
  const [progress, setProgress] = useState(0);
  const [batch, setBatch] = useState(null);
  const [stored, setStored] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [leadCode, setLeadCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!searching) return;
    const t = setInterval(() => {
      setProgress((p) => Math.min(95, p + (p < 55 ? 2.4 : p < 80 ? 1.1 : 0.4)));
    }, 1000);
    return () => clearInterval(t);
  }, [searching]);

  const fetchStored = async () => {
    try {
      const { data } = await http.get("/leads");
      setStored(data.leads || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (enabled) fetchStored();
  }, [enabled]);

  const finish = (j) => {
    setSearching(false);
    refreshCredits();
    if (j.status === "done") {
      setBatch(j.leads);
      toast.success(j.trial ? "Here's your free trial lead!" : `${j.leads.length} new leads found.`);
      fetchStored();
    } else if (j.status === "empty") {
      setBatch(null);
      toast.message("No active leads found — your coin was not used. Try a broader area or service.");
    } else {
      setBatch(null);
      toast.error("Lead search failed — your coin was refunded.");
    }
  };

  const poll = (jobId, started) => async () => {
    try {
      const { data: j } = await http.get(`/leads/job/${jobId}`);
      if (j.status !== "running") return finish(j);
      if (Date.now() - started > 240000) {
        setSearching(false);
        toast.error("Lead search timed out — your coin was refunded. Please try again.");
        refreshCredits();
        return;
      }
      setTimeout(poll(jobId, started), 3000);
    } catch {
      setSearching(false);
      toast.error("Lead search failed. Please try again.");
      refreshCredits();
    }
  };

  const search = async () => {
    if (!service.trim() || !location.trim()) {
      toast.error("Enter a service and a city or ZIP.");
      return;
    }
    setSearching(true);
    setProgress(6);
    try {
      const { data } = await http.post("/leads/search", { service: service.trim(), location: location.trim(), lead_type: leadType });
      toast.message("Searching the web for real leads — this can take up to a couple of minutes.");
      setTimeout(poll(data.job_id, Date.now()), 3000);
    } catch (e) {
      setSearching(false);
      toast.error(e.response?.status === 402 ? "You're out of lead coins — grab a pack to unlock more leads." : e.response?.data?.detail || "Lead search failed.");
    }
  };

  const removeLead = async (lead) => {
    if (!window.confirm("Delete this lead now? This can't be undone.")) return;
    try {
      await http.delete(`/leads/${lead.id}`);
      setStored((s) => s.filter((l) => l.id !== lead.id));
      setBatch((b) => (b ? b.filter((l) => l.id !== lead.id) : b));
      toast.success("Lead deleted.");
    } catch {
      toast.error("Could not delete that lead.");
    }
  };

  const redeemLeadCode = async () => {
    if (!leadCode.trim()) return;
    setRedeeming(true);
    try {
      const { data } = await http.post("/leads/redeem", { code: leadCode.trim() });
      toast.success(data.message || "Lead coins added!");
      setLeadCode("");
      refreshCredits();
    } catch (e) {
      toast.error(e.response?.data?.detail || "That lead code is not valid.");
    } finally {
      setRedeeming(false);
    }
  };

  const batchIds = new Set((batch || []).map((b) => b.id));
  return {
    service, setService, location, setLocation, leadType, setLeadType,
    searching, progress, batch, saved: stored.filter((l) => !batchIds.has(l.id)), now,
    search, removeLead, leadCode, setLeadCode, redeeming, redeemLeadCode,
    leadCoins: credits?.lead_coins ?? 0, trialAvailable: credits?.lead_trial_available,
  };
}
